import cookies from "js-cookie";
import React, { Fragment, useEffect, useMemo, useState } from "react";

import useDevice from "@hooks/useDevice";
import { getComparator } from "@utils/table";
import { TablePagination } from "@mui/material";

import type { Direction } from "@enums/direction";

type TableFooterProps<T> = {
  order: Direction;
  total?: number;
  withSort?: boolean;
  direction?: Direction;
  withPagination?: boolean;
  tableData: T[];
  rows?: T[];
  orderBy: keyof T;
  setVisibleRows?: (rows: T[]) => void;
};

function TableFooter<T>({
  rows,
  order,
  orderBy,
  direction,
  tableData,
  setVisibleRows,
  withSort = true,
  withPagination = false,
}: TableFooterProps<T>) {
  const rowsPerPageCount = Number(cookies.get("rowsPerPage")) || 15;
  const [rowsPerPage, setRowsPerPage] = useState(
    withPagination ? rowsPerPageCount : 100000000000, // all (while -1 is not working)
  );

  const [page, setPage] = useState(0);
  const { isMobile } = useDevice();

  const visibleRows = useMemo(() => {
    if (withSort) {
      return [...rows!]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    } else {
      return [...rows!].slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
    }
  }, [order, orderBy, page, rowsPerPage, tableData, direction]);

  useEffect(() => {
    if (setVisibleRows) setVisibleRows(visibleRows);
  }, [order, orderBy, page, rowsPerPage, tableData, direction]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    cookies.set("rowsPerPage", event.target.value);
  };

  return (
    <Fragment>
      {withPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
          colSpan={3}
          count={rows!.length}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
          component="div"
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Fragment>
  );
}

export default TableFooter;
